import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { useToast } from '../../../../components/ui/use-toast'
import { usersService } from '../../../../../app/services/usersService'
import { CreateSellerBody } from '../../../../../app/services/usersService/createSeller'

const createSellerSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  cpf: z.string().min(11, 'CPF deve ter 11 dígitos').max(14, 'CPF inválido'),
  email: z.string().email('Email inválido'),
  phone: z.string().min(10, 'Telefone deve ter pelo menos 10 dígitos'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  birthDate: z.string().optional(),
  instagram: z.string().optional(),
  marketing: z.boolean().default(false),
  rg: z.object({
    number: z.string().optional(),
    organ: z.string().optional(),
    uf: z.string().optional(),
  }).optional(),
  address: z.object({
    zipCode: z.string().min(8, 'CEP deve ter 8 dígitos').max(9, 'CEP inválido'),
    street: z.string().min(1, 'Rua é obrigatória'),
    number: z.string().min(1, 'Número é obrigatório'),
    complement: z.string().optional(),
    neighborhood: z.string().min(1, 'Bairro é obrigatório'),
    city: z.string().min(1, 'Cidade é obrigatória'),
    state: z.string().min(2, 'Estado é obrigatório').max(2, 'Estado deve ter 2 letras'),
  }),
  bankAccount: z.object({
    accountType: z.string().min(1, 'Tipo de conta é obrigatório'),
    bank: z.string().min(1, 'Banco é obrigatório'),
    agency: z.string().min(1, 'Agência é obrigatória'),
    account: z.string().min(1, 'Conta é obrigatória'),
  }),
  teamCode: z.string().optional(),
})

type CreateSellerFormData = z.infer<typeof createSellerSchema>

interface UseCreateSellerModalControllerProps {
  onClose: () => void
}

export function useCreateSellerModalController({
  onClose,
}: UseCreateSellerModalControllerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const form = useForm<CreateSellerFormData>({
    resolver: zodResolver(createSellerSchema),
    defaultValues: {
      name: '',
      cpf: '',
      email: '',
      phone: '',
      password: '',
      birthDate: '',
      instagram: '',
      marketing: false,
      rg: {
        number: '',
        organ: '',
        uf: '',
      },
      address: {
        zipCode: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
      },
      bankAccount: {
        accountType: '',
        bank: '',
        agency: '',
        account: '',
      },
      teamCode: '',
    },
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (data: CreateSellerFormData) => {
      const payload: CreateSellerBody = {
        ...data,
        rg: data.rg?.number && data.rg?.organ && data.rg?.uf ? {
          number: data.rg.number,
          organ: data.rg.organ,
          uf: data.rg.uf,
        } : undefined,
      }
      return usersService.createSeller(payload)
    },
  })

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      await mutateAsync(data)
      
      toast({
        title: 'Vendedor criado com sucesso!',
        description: 'O vendedor foi criado e pode fazer login na plataforma.',
      })
      
      queryClient.invalidateQueries({ queryKey: ['users', 'sellers'] })
      form.reset()
      handleCloseModal()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar vendedor',
        description: 'Ocorreu um erro ao tentar criar o vendedor. Tente novamente.',
      })
    }
  })

  const handleOpenModal = () => {
    setIsOpen(true)
  }

  const handleCloseModal = () => {
    setIsOpen(false)
    form.reset()
    onClose()
  }

  return {
    isOpen,
    form,
    isPending,
    handleSubmit,
    handleOpenModal,
    handleCloseModal,
  }
}